
import { defineComponent } from 'vue'
import Breadcrumbs from '../../../components/Breadcrumbs.vue'
import SpinnerLoader from '../../../components/SpinnerLoader.vue'

// Компоненты модуля
import { Filters, Summary, MetricsTable, KpiSummary } from '../components/dashboard/index'
import ShiftDataSidebar from '../components/dashboard/ShiftDataSidebar.vue'
import { PlanList, PlanSettingsModal, PlanSettings } from '../components/plan/index'
import PlanEditWrapper from './PlanEditWrapper.vue'

// Типы модуля
import {
    DashboardFilters,
    FilterOptions,
    SummaryData,
    ChartData,
    Shift,
    Plan,
    Task,
    Branch,
    User,
    CrmUser,
} from '../types/index'

// Сервисы модуля
import { planService, shiftService } from '../services/index'
import { getConnectionStatus } from '@/api/connectors/yClients'
import { CONNECTOR_TYPES } from '@root/src/vars/general'

export default defineComponent({
    name: 'BeautyAutopilot',
    components: {
        SpinnerLoader,
        Breadcrumbs,
        Filters,
        Summary,
        MetricsTable,
        KpiSummary,
        ShiftDataSidebar,
        PlanList,
        PlanSettingsModal,
        PlanSettings,
        PlanEditWrapper,
    },
    data() {
        return {
            // Состояние
            loading: false,
            chartLoading: false,
            summaryLoading: false,
            shiftsLoading: false,
            isFirstLoad: true,

            // Навигация
            activeTab: 'overview',
            tabs: [
                {
                    id: 'overview',
                    name: 'Обзор',
                    hintLink: 'https://dataheroes.platrum.ru/help?slug=5385-beauty-autopilot',
                },
                { id: 'plans', name: 'Планы' },
            ],

            // Фильтры
            filters: {
                plan: '',
                period: {
                    month: new Date().getMonth() + 1,
                    year: new Date().getFullYear(),
                    label: '',
                },
                branch: '',
                channel: '',
                administrator: '',
            } as DashboardFilters,

            // Данные
            kpiChartData: null as ChartData | null,
            summaryData: null as SummaryData | null,
            shifts: [] as Shift[],
            chartPeriods: [], // Инициализируем пустыми массивами
            metricsData: [], // Инициализируем пустыми массивами
            filterOptions: {
                plans: [],
                branches: [],
                channels: [],
                administrators: [],
            } as FilterOptions,

            // Модальные окна
            showCreatePlanModal: false,
            showCreateShiftModal: false,
            showShiftSidebar: false,

            // Данные для сайдбара смены
            selectedShiftData: null as any,
            selectedShift: null as Shift | null,
            showPlanSettings: false,
            selectedPlan: null as Plan | null,
            showPlanEdit: false,

            // Пагинация
            currentPage: 1,
            totalPages: 1,
            plansCurrentPage: 1,
            plansTotalPages: 1,

            // Права доступа
            canDeleteShift: true,
            canEditPlan: true,
            canDeletePlan: true,

            // Данные для планов
            plans: [] as Plan[],
            plansLoading: false,
            availableTasks: [] as Task[],
            availableBranches: [] as Branch[],
            availableChannels: [] as any[],
            // availableUsers теперь получаем из store через computed свойство projectUsers
            availableCrmUsers: [] as CrmUser[],
            dailyRevenuePlan: 100000,
        }
    },
    computed: {
        project(): any {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id) || {}
        },
        projectId(): string {
            return this.project?.id || this.$router.currentRoute.params.id || ''
        },
        breadcrumbs(): any[] {
            return [
                {
                    text: this.project?.name || 'Проект',
                    to: `/project/${this.$router.currentRoute.params.id}`,
                },
                {
                    text: 'Beauty Автопилот',
                    to: '',
                    disabled: true,
                },
            ]
        },
        canCreatePlan(): boolean {
            // TODO: Временно отключено - права будут настроены позже
            return true
        },
        canCreateShift(): boolean {
            // TODO: Временно отключено - права будут настроены позже
            return true
        },
        projectUsers(): any[] {
            // Получаем пользователей проекта DH из store
            return this.project?.users || []
        },
        currentUser(): any {
            return this.$store.getters.myUser
        },
        activePlans(): any[] {
            return this.plans.filter(plan => plan.isActive === true)
        },
        currentPlan(): any {
            const selectedPlanId = (this.filters as any).plan
            if (selectedPlanId) {
                return this.plans.find(plan => plan.id === selectedPlanId) || null
            }
            return this.activePlans[0] || null
        },
        activePlan(): any {
            // Backward-compatible alias: активный план в экранах = выбранный текущий план.
            return this.currentPlan
        },

        // Проверка прав доступа
        isManager(): boolean {
            // Руководитель - тот у которого есть право "владелец" на проекте
            const currentUser = this.currentUser
            if (!currentUser || !this.project) return false
            // Проверяем, есть ли у пользователя право "владелец" на проекте
            return (
                this.project.users?.some(
                    (user: any) => user.id === currentUser.sub && user.role.id === 'owner'
                ) || false
            )
        },

        isAdministrator(): boolean {
            // Администратор - это просто не управляющий (не владелец проекта)
            return !this.isManager
        },

        // Доступные вкладки в зависимости от роли
        availableTabs(): any[] {
            if (this.isManager) {
                // Руководитель видит все вкладки
                return this.tabs
            } else if (this.isAdministrator) {
                // Администратор видит только вкладку "Обзор"
                return this.tabs.filter(tab => tab.id === 'overview')
            }

            return []
        },

        // Может ли пользователь редактировать смены других пользователей
        canEditOtherUsersShifts(): boolean {
            return this.isManager
        },

        // Может ли пользователь создавать планы
        canCreatePlans(): boolean {
            return this.isManager
        },

        // Может ли пользователь создавать смены
        canCreateShifts(): boolean {
            // И руководитель, и администратор могут создавать смены
            return this.isManager || this.isAdministrator
        },
    },
    methods: {
        openHintLink(link: string) {
            if (link) {
                window.open(link, '_blank')
            }
        },

        // Методы для работы с правами доступа
        getAvailableAdministrators() {
            // И руководитель, и администратор могут видеть всех пользователей
            return (
                this.projectUsers?.map((user: any) => ({
                    id: user.id,
                    name: user.name || user.username,
                    number: 0,
                    isSelected: false,
                })) || []
            )
        },

        // Может ли пользователь редактировать смену
        canEditShift(shift: any): boolean {
            if (this.isManager) {
                // Руководитель может редактировать все смены
                return true
            } else if (this.isAdministrator) {
                // Администратор может редактировать только свои смены
                return shift.administratorId === this.currentUser?.sub
            }
            return false
        },

        // Вспомогательный метод для получения плана по ID
        getPlanById(planId: string): any {
            return this.plans.find(plan => plan.id === planId) || null
        },
        formatDate(date: Date | string | null): string {
            if (!date) return ''
            const d = new Date(date)
            return d.toLocaleDateString('ru-RU')
        },
        sortPlanOptions(planOptions: any[]) {
            return [...planOptions].sort((a: any, b: any) => {
                const aActive = a.isActive ? 1 : 0
                const bActive = b.isActive ? 1 : 0
                if (aActive !== bActive) {
                    return bActive - aActive
                }
                return String(a.name || '').localeCompare(String(b.name || ''), 'ru')
            })
        },

        async loadFilterOptions() {
            try {
                // Получаем все смены для определения планов с сменами
                const allShifts = await shiftService.getShifts({}, this.projectId)
                const plansWithShifts = [...new Set(allShifts.map((shift: any) => shift.planId))]
                const activePlan = this.activePlan
                const activePlanIds = new Set(
                    (this.activePlans || []).map((plan: any) => plan.id)
                )

                // Формируем список планов для фильтра
                const planOptions: any[] = []
                const addedPlanIds = new Set<string>()

                // Добавляем все активные планы
                this.plans.forEach(plan => {
                    if (activePlanIds.has(plan.id) && !addedPlanIds.has(plan.id)) {
                        planOptions.push({
                            id: plan.id,
                            name: plan.name,
                            displayName: plan.name,
                            isActive: true,
                            isSelected: plan.id === (this.filters as any).plan,
                            hasShifts: plansWithShifts.includes(plan.id),
                        })
                        addedPlanIds.add(plan.id)
                    }
                })

                // Добавляем планы со сменами, если их еще нет в списке
                this.plans.forEach(plan => {
                    if (plansWithShifts.includes(plan.id) && !addedPlanIds.has(plan.id)) {
                        planOptions.push({
                            id: plan.id,
                            name: plan.name,
                            displayName: plan.name,
                            isActive: false,
                            isSelected: plan.id === (this.filters as any).plan,
                            hasShifts: true,
                        })
                        addedPlanIds.add(plan.id)
                    }
                })

                // Обновляем только список планов, не затрагивая другие опции
                this.filterOptions = {
                    ...this.filterOptions,
                    plans: this.sortPlanOptions(planOptions),
                } as any

                // Автоматически выбираем активный план только при первой загрузке
                if (this.isFirstLoad && activePlan) {
                    ;(this.filters as any).plan = activePlan.id
                    // Обновляем опции фильтров для активного плана
                    this.updateFilterOptionsForPlan(activePlan)
                    this.isFirstLoad = false
                } else if (this.isFirstLoad && !activePlan) {
                    ;(this.filters as any).plan = ''
                    this.isFirstLoad = false
                } else if (activePlan && !(this.filters as any).plan) {
                    ;(this.filters as any).plan = activePlan.id
                }
            } catch (error) {
                // В случае ошибки используем текущие смены
                this.loadFilterOptionsFromCurrentShifts()
            }
        },

        loadFilterOptionsFromCurrentShifts() {
            // Резервный метод - используем текущие смены в памяти
            const plansWithShifts = [...new Set(this.shifts.map((shift: any) => shift.planId))]
            const activePlan = this.activePlan
            const activePlanIds = new Set(
                (this.activePlans || []).map((plan: any) => plan.id)
            )

            // Формируем список планов для фильтра
            const planOptions: any[] = []
            const addedPlanIds = new Set<string>()

            // Добавляем все активные планы
            this.plans.forEach(plan => {
                if (activePlanIds.has(plan.id) && !addedPlanIds.has(plan.id)) {
                    planOptions.push({
                        id: plan.id,
                        name: plan.name,
                        displayName: plan.name,
                        isActive: true,
                        isSelected: plan.id === (this.filters as any).plan,
                        hasShifts: plansWithShifts.includes(plan.id),
                    })
                    addedPlanIds.add(plan.id)
                }
            })

            // Добавляем планы со сменами, если их еще нет в списке
            this.plans.forEach(plan => {
                if (plansWithShifts.includes(plan.id) && !addedPlanIds.has(plan.id)) {
                    planOptions.push({
                        id: plan.id,
                        name: plan.name,
                        displayName: plan.name,
                        isActive: false,
                        isSelected: plan.id === (this.filters as any).plan,
                        hasShifts: true,
                    })
                    addedPlanIds.add(plan.id)
                }
            })

            // Обновляем только список планов, не затрагивая другие опции
            this.filterOptions = {
                ...this.filterOptions,
                plans: this.sortPlanOptions(planOptions),
            } as any

            // Автоматически выбираем активный план только при первой загрузке
            if (this.isFirstLoad && activePlan) {
                ;(this.filters as any).plan = activePlan.id
                // Обновляем опции фильтров для активного плана
                this.updateFilterOptionsForPlan(activePlan)
                this.isFirstLoad = false
            } else if (this.isFirstLoad && !activePlan) {
                ;(this.filters as any).plan = ''
                this.isFirstLoad = false
            } else if (activePlan && !(this.filters as any).plan) {
                ;(this.filters as any).plan = activePlan.id
            }
        },

        updateFilterOptionsForPlan(plan: any) {
            // Обновляем опции фильтров для выбранного плана
            this.filterOptions = {
                ...this.filterOptions,
                branches:
                    plan.branchIds?.map((branchId: string) => ({
                        id: branchId,
                        name: branchId,
                        number: 0,
                        isSelected: false,
                    })) || [],
                channels: [
                    {
                        id: 'general',
                        name: 'general',
                        number: 0,
                        isSelected: false,
                    },
                ],
                administrators: this.getAvailableAdministrators(),
            } as any

            // Устанавливаем значения по умолчанию для фильтров - "все" для салона и канала
            this.filters.branch = '' // Пустая строка означает "все"
            this.filters.channel = '' // Пустая строка означает "все"

            // Выбираем администратора по умолчанию
            // И руководитель, и администратор могут видеть всех пользователей
            this.filters.administrator = this.currentUser?.sub || this.projectUsers?.[0]?.id || ''
        },

        // Преобразуем данные смен в формат таблицы метрик
        convertShiftsToMetricsData() {
            if (this.shifts.length === 0) {
                this.chartPeriods = []
                // Таблица должна быть доступна всегда: при отсутствии смен строим строки из активного плана
                if (this.activePlan) {
                    this.metricsData = this.buildMetricsFromPlanOnly()
                } else {
                    this.metricsData = []
                }
                return
            }

            // Проверяем, есть ли в планах филиалы и каналы (проверяем все планы смен)
            let hasBranchesInPlan = false
            let hasChannelsInPlan = false

            this.shifts.forEach((shift: any) => {
                const plan = this.getPlanById(shift.planId)
                if (plan) {
                    if (plan.branchIds && plan.branchIds.length > 0) {
                        hasBranchesInPlan = true
                    }
                }
            })

            // Анализируем уникальные значения для определения необходимости строк
            const uniqueBranches = new Set()
            const uniqueChannels = new Set()
            this.shifts.forEach((shift: any) => {
                if (shift.branchId) uniqueBranches.add(shift.branchId)
                if (shift.channelId) uniqueChannels.add(shift.channelId)
            })

            // Создаем периоды из данных смен и сортируем по дате
            this.chartPeriods = this.shifts
                .map((shift: any) => {
                    const plan = this.getPlanById(shift.planId)

                    let salonName = 'A'
                    if (plan && plan.branches && shift.branchId) {
                        const branch = plan.branches.find((b: any) => b.id === shift.branchId)
                        salonName = branch ? branch.name : shift.branchId
                    } else if (shift.branchId) {
                        salonName = shift.branchId
                    }


                    // Получаем имя администратора из списка пользователей проекта
                    let administratorName = ''
                    if (shift.administratorId) {
                        const user = this.projectUsers.find((u: any) => u.id === shift.administratorId)
                        administratorName = user ? user.name || user.username || '' : ''
                    }

                    // Отладка: проверяем результат

                    return {
                        id: shift.id,
                        label: new Date(shift.date).toLocaleDateString('ru-RU', {
                            day: 'numeric',
                            month: 'short',
                        }),
                        date: new Date(shift.date),
                        salon: salonName,
                        channel: 'general',
                        administratorName: administratorName,
                    }
                })
                .sort((a: any, b: any) => a.date.getTime() - b.date.getTime())

            // Создаем метрики из данных смен
            this.metricsData = []

            // Строка "Салон" - только если есть филиалы в плане
            if (hasBranchesInPlan) {
                this.metricsData.push({
                    id: 'salon',
                    name: 'Салон',
                    type: 'text',
                    values: this.shifts.reduce((acc: any, shift: any) => {
                        const plan = this.getPlanById(shift.planId)
                        let branchName = 'A'
                        if (plan && plan.branches && shift.branchId) {
                            const branch = plan.branches.find((b: any) => b.id === shift.branchId)
                            branchName = branch ? branch.name : shift.branchId
                        } else if (shift.branchId) {
                            branchName = shift.branchId
                        }
                        acc[shift.id] = branchName
                        return acc
                    }, {}),
                })
            }


            // Выполнение дневного KPI
            this.metricsData.push({
                id: 'kpi_performance',
                name: 'Выполнение дневного KPI, %',
                type: 'percentage',
                values: this.shifts.reduce((acc: any, shift: any) => {
                    if (!shift || !shift.id) {
                        return acc
                    }
                    const plan = this.getPlanById(shift.planId) || this.activePlan
                    const kpiValue = this.calculateDailyKpi(shift, plan)
                    acc[shift.id] = kpiValue
                    return acc
                }, {}),
            })

            // Собираем все уникальные метрики из indicators
            const allMetricKeys = new Set()
            this.shifts.forEach((shift: any) => {
                if (shift.indicators) {
                    Object.keys(shift.indicators).forEach((key: string) => {
                        allMetricKeys.add(key)
                    })
                }
            })

            // Получаем порядок задач из активного плана
            const planTaskOrder: string[] = []
            if (this.activePlan && this.activePlan.tasks) {
                this.activePlan.tasks.forEach((task: any) => {
                    const taskKey = task.id
                    if (allMetricKeys.has(taskKey)) {
                        planTaskOrder.push(taskKey)
                    }
                })
            }

            // Добавляем остальные задачи, которые есть в данных, но нет в плане
            allMetricKeys.forEach((metricKey: any) => {
                if (!planTaskOrder.includes(metricKey)) {
                    planTaskOrder.push(metricKey)
                }
            })

            // Добавляем метрики из indicators (задачи) в порядке плана
            planTaskOrder.forEach((metricKey: any) => {
                // Пропускаем dailyRevenue, так как она отображается отдельной строкой
                if (metricKey === 'dailyRevenue') {
                    return
                }

                // Находим задачу в availableTasks по ID
                const task = this.availableTasks.find((t: any) => t.id === metricKey)

                let metricName = metricKey
                let metricType = 'number'

                if (task) {
                    metricName = task.name
                    // Определяем тип на основе unit задачи
                    if (task.unit === 'P' || task.unit === 'руб') {
                        metricType = 'currency'
                    } else if (task.unit === 'шт' || task.unit === 'count') {
                        metricType = 'count'
                    } else if (task.unit === 'балл' || task.unit === 'баллы') {
                        metricType = 'decimal'
                    }
                }

                this.metricsData.push({
                    id: metricKey,
                    name: metricName,
                    description: task ? task.description || '' : '',
                    type: metricType,
                    hasTooltip: true,
                    norm: 0, // Норма будет рассчитываться индивидуально в getValueColorClass
                    values: this.shifts.reduce((acc: any, shift: any) => {
                        const value = shift.indicators?.[metricKey]
                        acc[shift.id] = typeof value === 'string' ? parseFloat(value) || 0 : value || 0
                        return acc
                    }, {}),
                })
            })

            // Дневная выручка (из поля dailyRevenue, не из indicators)
            // Создаем метрику дневной выручки с нормами для каждой смены
            this.metricsData.push({
                id: 'dailyRevenue',
                name: 'Дневная выручка (р/с+нал), P',
                type: 'currency',
                isBold: true,
                norm: 0, // Будет пересчитано в getValueColorClass
                values: this.shifts.reduce((acc: any, shift: any) => {
                    acc[shift.id] = parseFloat(shift.dailyRevenue) || 0
                    return acc
                }, {}),
            })

            // Премия за уровень дневной выручки
            this.metricsData.push({
                id: 'daily_revenue_bonus',
                name: 'Премия за уровень дневной выручки, P',
                type: 'currency',
                norm: 0, // Не используется для цветовой индикации
                values: this.shifts.reduce((acc: any, shift: any) => {
                    const plan = this.getPlanById(shift.planId)
                    const bonus = this.calculateDailyRevenueBonus(
                        parseFloat(shift.dailyRevenue) || 0,
                        plan,
                        shift.branchId
                    )
                    acc[shift.id] = bonus
                    return acc
                }, {}),
            })

            // Бонус за выполнение KPI
            this.metricsData.push({
                id: 'kpi_bonus',
                name: 'Бонус за выполнение KPI, P',
                type: 'currency',
                norm: 0, // Не используется для цветовой индикации
                values: this.shifts.reduce((acc: any, shift: any) => {
                    const plan = this.getPlanById(shift.planId)
                    const dailyKpi = this.calculateDailyKpi(shift, plan)
                    acc[shift.id] = this.calculateKpiBonus(dailyKpi, plan)
                    return acc
                }, {}),
            })

            // Фиксированная часть
            this.metricsData.push({
                id: 'fixed_part',
                name: 'Фиксированная часть, P',
                type: 'currency',
                norm: 0, // Не используется для цветовой индикации
                values: this.shifts.reduce((acc: any, shift: any) => {
                    const plan = this.getPlanById(shift.planId)
                    acc[shift.id] = plan?.salary?.fixedPart || 0
                    return acc
                }, {}),
            })

            // Итого начислений (сумма всех бонусов + фиксированная часть)
            this.metricsData.push({
                id: 'total_accruals',
                name: 'Итого начислений, P',
                type: 'currency',
                norm: 0, // Не используется для цветовой индикации
                isBold: true,
                values: this.shifts.reduce((acc: any, shift: any) => {
                    const plan = this.getPlanById(shift.planId)
                    const dailyRevenue = parseFloat(shift.dailyRevenue) || 0
                    const dailyKpi = this.calculateDailyKpi(shift, plan)
                    const dailyRevenueBonus = this.calculateDailyRevenueBonus(
                        dailyRevenue,
                        plan,
                        shift.branchId
                    )
                    const kpiBonus = this.calculateKpiBonus(dailyKpi, plan)
                    const fixedPart = plan?.salary?.fixedPart || 0
                    acc[shift.id] = dailyRevenueBonus + kpiBonus + fixedPart
                    return acc
                }, {}),
            })
        },

        // Строим метрики только из плана (без смен) — для отображения норм при выбранном филиале без данных
        buildMetricsFromPlanOnly() {
            const plan = this.activePlan
            if (!plan) return []

            const hasBranchesInPlan = !!(plan.branchIds && plan.branchIds.length > 0)
            const metrics = []

            if (hasBranchesInPlan) {
                metrics.push({
                    id: 'salon',
                    name: 'Салон',
                    type: 'text',
                    values: {},
                })
            }

            metrics.push({
                id: 'kpi_performance',
                name: 'Выполнение дневного KPI, %',
                type: 'percentage',
                values: {},
            })

            if (plan.tasks) {
                plan.tasks.forEach((task: any) => {
                    if (task.id === 'dailyRevenue') return
                    let metricType = 'number'
                    if (task.unit === 'P' || task.unit === 'руб') metricType = 'currency'
                    else if (task.unit === 'шт' || task.unit === 'count') metricType = 'count'
                    else if (task.unit === 'балл' || task.unit === 'баллы') metricType = 'decimal'

                    metrics.push({
                        id: task.id,
                        name: task.name || task.id,
                        description: task.description || '',
                        type: metricType,
                        hasTooltip: true,
                        norm: 0,
                        values: {},
                    })
                })
            }

            metrics.push({
                id: 'dailyRevenue',
                name: 'Дневная выручка (р/с+нал), P',
                type: 'currency',
                isBold: true,
                norm: 0,
                values: {},
            })

            metrics.push({
                id: 'daily_revenue_bonus',
                name: 'Премия за уровень дневной выручки, P',
                type: 'currency',
                norm: 0,
                values: {},
            })

            metrics.push({
                id: 'kpi_bonus',
                name: 'Бонус за выполнение KPI, P',
                type: 'currency',
                norm: 0,
                values: {},
            })

            metrics.push({
                id: 'fixed_part',
                name: 'Фиксированная часть, P',
                type: 'currency',
                norm: 0,
                values: {},
            })

            metrics.push({
                id: 'total_accruals',
                name: 'Итого начислений, P',
                type: 'currency',
                norm: 0,
                isBold: true,
                values: {},
            })

            return metrics
        },

        // Рассчитываем дневной KPI как средневзвешенный процент выполнения плана задач
        calculateDailyKpi(shift: any, plan?: any): number {
            const targetPlan = plan || this.activePlan
            if (!targetPlan || !targetPlan.tasks || !Array.isArray(targetPlan.tasks) || targetPlan.tasks.length === 0) {
                return 0
            }
            
            // Проверяем наличие indicators
            if (!shift || !shift.indicators || typeof shift.indicators !== 'object') {
                return 0
            }

            let totalWeightedPercentage = 0
            let totalWeight = 0

            // Проходим по всем задачам из плана смены
            targetPlan.tasks.forEach((task: any) => {
                if (!task || !task.id) {
                    return // Пропускаем некорректные задачи
                }
                
                const taskId = task.id
                const significance = task.significance || 0 // значимость задачи
                
                // Пропускаем задачи с нулевой значимостью
                if (significance === 0) {
                    return
                }
                
                const actualValue = shift.indicators[taskId]

                // Проверяем, что значение присутствует (может быть 0, что валидно)
                if (actualValue !== undefined && actualValue !== null && actualValue !== '') {
                    // Получаем норму для задачи (учитываем branchId, channelId всегда 'general')
                    const branchId = shift.branchId || 'general'
                    const channelId = 'general'

                    let norm = 0
                    if (task.norms && task.norms[branchId] && task.norms[branchId][channelId] !== undefined) {
                        norm = task.norms[branchId][channelId]
                    } else if (
                        task.norms &&
                        task.norms['general'] &&
                        task.norms['general'][channelId] !== undefined
                    ) {
                        norm = task.norms['general'][channelId]
                    } else if (
                        task.norms &&
                        task.norms[branchId] &&
                        task.norms[branchId]['general'] !== undefined
                    ) {
                        norm = task.norms[branchId]['general']
                    } else if (
                        task.norms &&
                        task.norms['general'] &&
                        task.norms['general']['general'] !== undefined
                    ) {
                        norm = task.norms['general']['general']
                    }

                    // Получаем максимальный лимит для задачи
                    let maxLimit = 100 // по умолчанию 100%
                    if (
                        task.limits &&
                        task.limits[branchId] &&
                        task.limits[branchId][channelId] !== undefined
                    ) {
                        maxLimit = task.limits[branchId][channelId]
                    } else if (
                        task.limits &&
                        task.limits['general'] &&
                        task.limits['general'][channelId] !== undefined
                    ) {
                        maxLimit = task.limits['general'][channelId]
                    } else if (
                        task.limits &&
                        task.limits[branchId] &&
                        task.limits[branchId]['general'] !== undefined
                    ) {
                        maxLimit = task.limits[branchId]['general']
                    } else if (
                        task.limits &&
                        task.limits['general'] &&
                        task.limits['general']['general'] !== undefined
                    ) {
                        maxLimit = task.limits['general']['general']
                    }

                    // Рассчитываем процент выполнения с учетом лимита
                    if (norm > 0) {
                        const actualNum =
                            typeof actualValue === 'string' ? parseFloat(String(actualValue)) : Number(actualValue) || 0
                        if (!isNaN(actualNum)) {
                            const rawPercentage = (actualNum / norm) * 100
                            const cappedPercentage = Math.min(rawPercentage, maxLimit)

                            // Добавляем к средневзвешенному
                            totalWeightedPercentage += cappedPercentage * significance
                            totalWeight += significance
                        }
                    }
                }
            })

            // Если нет задач с весом, возвращаем 0
            if (totalWeight === 0) {
                return 0
            }

            const result = totalWeightedPercentage / totalWeight
            return Math.round(result * 100) / 100 // округляем до 2 знаков
        },

        async loadShifts() {
            try {
                this.shiftsLoading = true

                // И руководитель, и администратор могут видеть смены всех пользователей
                const administratorId = this.filters.administrator || undefined

                const filters = {
                    planId: (this.filters as any).plan || this.activePlan?.id || '',
                    month: this.filters.period.month,
                    year: this.filters.period.year,
                    branch: this.filters.branch || undefined,
                    channel: this.filters.channel || undefined,
                    administratorId: administratorId,
                }
                const data = await shiftService.getShifts(filters, this.projectId) // Передаем projectId

                this.shifts = data
                // После загрузки смен обновляем данные для таблицы метрик
                this.convertShiftsToMetricsData()
                // Рассчитываем данные сводки
                this.calculateSummaryData()
            } catch (error) {
            } finally {
                this.shiftsLoading = false
            }
        },

        onFiltersChange(newFilters) {
            // Если изменился план, обновляем опции фильтров сначала
            const selectedPlan = this.plans.find(plan => plan.id === (newFilters as any).plan)
            if (selectedPlan) {
                this.updateFilterOptionsForPlan(selectedPlan)
            } else {
                // Если план не выбран, очищаем опции филиалов и каналов, но оставляем всех администраторов
                this.filterOptions = {
                    ...this.filterOptions,
                    branches: [],
                    channels: [],
                    administrators:
                        this.projectUsers?.map((user: any) => ({
                            id: user.id,
                            name: user.name || user.username,
                            number: 0,
                            isSelected: false,
                        })) || [],
                } as any
            }

            // Обновляем фильтры после обновления опций
            this.filters = { ...newFilters }

            // Если план не выбран, сбрасываем значения фильтров
            if (!selectedPlan) {
                this.filters.branch = ''
                this.filters.channel = ''
                this.filters.administrator = ''
            }

            this.loadShifts()
        },

        calculateSummaryData() {
            if (this.shifts.length === 0) {
                this.summaryData = {
                    dailyRevenueBonus: 0,
                    fixedPart: 0,
                    kpiBonus: 0,
                    totalAccruals: 0,
                    shifts: [],
                    kpiPerformance: 0,
                }
                return
            }

            // Рассчитываем данные по каждой смене
            const shiftsWithCalculations = this.shifts.map((shift: any) => {
                const plan = this.getPlanById(shift.planId)
                const dailyRevenue = shift.dailyRevenue || 0
                const dailyKpi = this.calculateDailyKpi(shift, plan)

                // Рассчитываем премию за дневную выручку
                const dailyRevenueBonus = this.calculateDailyRevenueBonus(dailyRevenue, plan, shift.branchId)

                // Фиксированная часть
                const fixedPart = plan?.salary?.fixedPart || 0

                // Рассчитываем бонус за KPI
                const kpiBonus = this.calculateKpiBonus(dailyKpi, plan)

                // Общая сумма начислений
                const totalAccruals = dailyRevenueBonus + fixedPart + kpiBonus

                return {
                    ...shift,
                    dailyKpi,
                    dailyRevenueBonus,
                    fixedPart,
                    kpiBonus,
                    totalAccruals,
                }
            })

            // Рассчитываем общие показатели
            const totalDailyRevenueBonus = shiftsWithCalculations.reduce(
                (sum: number, shift: any) => sum + shift.dailyRevenueBonus,
                0
            )
            const totalFixedPart = shiftsWithCalculations.reduce(
                (sum: number, shift: any) => sum + shift.fixedPart,
                0
            )
            const totalKpiBonus = shiftsWithCalculations.reduce(
                (sum: number, shift: any) => sum + shift.kpiBonus,
                0
            )
            const totalAccruals = totalDailyRevenueBonus + totalFixedPart + totalKpiBonus

            // Рассчитываем общий KPI (средний по всем сменам)
            const averageKpi =
                shiftsWithCalculations.length > 0
                    ? shiftsWithCalculations.reduce(
                          (sum: number, shift: any) => sum + (shift.dailyKpi || 0),
                          0
                      ) / shiftsWithCalculations.length
                    : 0

            this.summaryData = {
                dailyRevenueBonus: totalDailyRevenueBonus,
                fixedPart: totalFixedPart,
                kpiBonus: totalKpiBonus,
                totalAccruals,
                shifts: shiftsWithCalculations,
                kpiPerformance: Math.round(averageKpi * 100) / 100,
            }
        },

        // Расчет премии за дневную выручку на основе шкалы из плана
        calculateDailyRevenueBonus(dailyRevenue: number, plan?: any, branchId?: string): number {
            const targetPlan = plan || this.activePlan
            if (dailyRevenue <= 0) {
                return 0
            }

            let revenueScale: any[] = []

            // Определяем, какую шкалу использовать
            if (targetPlan?.revenueScale && branchId && targetPlan.revenueScale[branchId]) {
                // Для нескольких филиалов используем шкалу конкретного филиала
                revenueScale = targetPlan.revenueScale[branchId]
            } else if (targetPlan?.revenueScale && Array.isArray(targetPlan.revenueScale)) {
                // Для одного филиала или общего случая используем основную шкалу
                revenueScale = targetPlan.revenueScale
            } else if (targetPlan?.revenueScale && targetPlan.revenueScale['general']) {
                // Используем общую шкалу, если она есть
                revenueScale = targetPlan.revenueScale['general']
            } else {
                return 0
            }

            if (!Array.isArray(revenueScale) || revenueScale.length === 0) {
                return 0
            }

            // Сортируем шкалу по выручке по возрастанию
            const sortedScale = [...revenueScale].sort((a, b) => a.revenue - b.revenue)

            // Находим подходящий уровень (аналог ПОИСКПОЗ с параметром 1)
            let bonus = 0
            for (const scaleItem of sortedScale) {
                if (dailyRevenue >= scaleItem.revenue) {
                    bonus = scaleItem.bonus
                } else {
                    break
                }
            }

            return bonus
        },

        // Расчет бонуса за KPI на основе шкалы и номинального бонуса
        calculateKpiBonus(dailyKpiPercentage: number, plan?: any): number {
            const targetPlan = plan || this.activePlan
            if (
                !targetPlan?.kpiScale ||
                !Array.isArray(targetPlan.kpiScale) ||
                !targetPlan.salary?.kpiBonus ||
                dailyKpiPercentage <= 0
            ) {
                return 0
            }

            // Сортируем шкалу KPI по проценту выполнения по возрастанию
            const sortedKpiScale = [...targetPlan.kpiScale].sort((a, b) => a.completion - b.completion)

            // Находим подходящий уровень (аналог ПОИСКПОЗ с параметром 1)
            let bonusPercentage = 0
            for (const scaleItem of sortedKpiScale) {
                if (dailyKpiPercentage >= scaleItem.completion) {
                    bonusPercentage = scaleItem.bonusPercentage
                } else {
                    break
                }
            }

            // Рассчитываем бонус: номинальный бонус * процент от номинала / 100
            const nominalKpiBonus = targetPlan.salary.kpiBonus || 0
            const bonus = (nominalKpiBonus * bonusPercentage) / 100

            return Math.round(bonus * 100) / 100 // округляем до 2 знаков
        },

        onAddShift() {
            // Проверяем наличие опубликованного плана
            if (!this.activePlan) {
                return
            }

            // Создаем данные смены на основе активного плана
            this.selectedShift = null
            this.selectedShiftData = {
                date: new Date().toISOString().split('T')[0], // Текущая дата
                salon:
                    this.activePlan.branchIds && this.activePlan.branchIds.length > 0
                        ? this.activePlan.branchIds[0]
                        : '',
                channel:
                    this.activePlan.channelIds && this.activePlan.channelIds.length > 0
                        ? this.activePlan.channelIds[0]
                        : '',
                metrics: {},
                planId: this.activePlan.id,
                projectId: this.projectId,
                // Устанавливаем администратора из фильтра (выбранного пользователя)
                administratorId: this.filters.administrator || this.currentUser?.sub,
            }

            this.showShiftSidebar = true
        },

        openShiftSidebar(shift: Shift) {
            this.selectedShift = shift
            this.showShiftSidebar = true
        },

        closeShiftSidebar() {
            this.showShiftSidebar = false
            this.selectedShift = null
            this.selectedShiftData = null
        },

        async onShiftSidebarSubmit(shiftData: any) {
            try {
                // Обрабатываем данные смены
                const processedShiftData = { ...shiftData }

                // Извлекаем dailyRevenue из metrics и сохраняем как отдельное поле
                if (processedShiftData.metrics && processedShiftData.metrics.dailyRevenue !== undefined) {
                    processedShiftData.dailyRevenue = processedShiftData.metrics.dailyRevenue
                }

                // Переименовываем metrics в indicators для API
                if (processedShiftData.metrics) {
                    processedShiftData.indicators = processedShiftData.metrics
                    delete processedShiftData.metrics
                }

                if (this.selectedShift) {
                    // Редактирование существующей смены
                    const updateData = {
                        id: this.selectedShift.id,
                        projectId: this.projectId,
                        ...processedShiftData,
                    }

                    await shiftService.updateShift(updateData)
                } else {
                    // Создание новой смены
                    const createData = {
                        ...processedShiftData,
                        projectId: this.projectId,
                    }
                    await shiftService.createShift(createData)
                }
                this.showShiftSidebar = false
                this.selectedShift = null
                await this.loadShifts()
                await this.calculateSummaryData()
            } catch (error) {
                console.error('Ошибка при сохранении смены:', error)
                // Проверяем, является ли это ошибкой дублирования
                if (error instanceof Error && error.message === 'Смена с указанными данными уже существует') {
                    alert('Смена с указанными данными уже существует.')
                } else {
                    alert('Произошла ошибка при сохранении смены. Попробуйте еще раз.')
                }
            }
        },

        async onShiftSidebarDelete(shiftData: any) {
            try {
                if (!shiftData.id) {
                    console.error('Нельзя удалить смену без ID')
                    return
                }

                // Подтверждение удаления
                if (!confirm('Вы уверены, что хотите удалить эту смену?')) {
                    return
                }

                // Удаляем смену через API
                await shiftService.deleteShift(shiftData.id, this.projectId)

                // Обновляем данные в таблице
                await this.loadShifts()
                await this.calculateSummaryData()

                // Закрываем сайдбар
                this.showShiftSidebar = false
                this.selectedShiftData = null
            } catch (error) {
                console.error('Ошибка при удалении смены:', error)
                alert('Произошла ошибка при удалении смены')
            }
        },

        onPlanCreated(plan: any) {
            this.showCreatePlanModal = false
            // Обновить данные
            this.loadShifts()
        },

        onShiftSaved(shift: Shift) {
            this.showShiftSidebar = false
            // Обновить данные
            this.loadShifts()
        },

        onMetricChange(metric: string) {
            // Обработка изменения метрики в графике
        },

        onCellClick(cellData: any) {
            // Обработка клика по ячейке таблицы
            // Находим смену по ID периода
            const shiftId = cellData.period.id
            const shift = this.shifts.find((s: any) => s.id === shiftId)

            if (!shift) {
                return
            }

            // Преобразуем дату в формат для сайдбара (DD.MM.YYYY)
            const dateValue = new Date(shift.date).toLocaleDateString('ru-RU')

            // Находим названия филиала и канала из самой смены
            const branchName = (shift as any).branchId || (shift as any).branch_id || ''
            const channelName = (shift as any).channelId || (shift as any).channel_id || ''

            // Преобразуем данные смены в формат для сайдбара
            this.selectedShiftData = {
                id: shift.id, // Важно: передаем ID смены для редактирования
                date: dateValue,
                salon: branchName,
                channel: channelName,
                metrics: {
                    // Заполняем метрики из indicators, конвертируя строки в числа
                    ...(function() {
                        const metrics: Record<string, number> = {}
                        const indicators = (shift as any).indicators || {}
                        for (const [key, value] of Object.entries(indicators)) {
                            metrics[key] =
                                typeof value === 'string'
                                    ? parseFloat(value) || 0
                                    : typeof value === 'number'
                                    ? value
                                    : 0
                        }
                        // Добавляем daily_revenue как отдельную метрику из смены
                        metrics.dailyRevenue =
                            parseFloat((shift as any).dailyRevenue || (shift as any).daily_revenue) || 0
                        return metrics
                    })(),
                },
            }

            // Устанавливаем выбранную смену для редактирования
            this.selectedShift = shift

            this.showShiftSidebar = true
        },

        async onShiftDelete(shiftData: any) {
            // Обработка удаления смены
            try {
                // Здесь можно добавить вызов API для удаления
                // await shiftService.deleteShift(shiftData.id)

                // Обновляем данные в таблице
                this.loadShifts()

                this.showShiftSidebar = false
                this.selectedShiftData = null
            } catch (error) {
                // Обработка ошибки удаления
            }
        },

        onShiftClose() {
            // Закрытие сайдбара
            this.showShiftSidebar = false
            this.selectedShiftData = null
        },

        deleteShift(shift: Shift) {
            // TODO: Реализовать удаление смены
            this.loadShifts()
        },

        changePage(page: number) {
            this.currentPage = page
            this.loadShifts()
        },

        // Методы для работы с планами
        async loadPlans() {
            this.plansLoading = true
            try {
                const result = await planService.getPlans({}, this.projectId)
                this.plans = result

                this.plansTotalPages = 1
            } catch (error) {
                // Обработка ошибки загрузки планов
            } finally {
                this.plansLoading = false
            }
        },

        async loadPlanData() {
            try {
                // Проверяем статус коннектора yClients перед загрузкой CRM пользователей
                const yClientsConnector = this.$store.getters.projectById(this.projectId)?.connectors?.find(
                    (connector: any) => connector.connectorType === CONNECTOR_TYPES.yClients
                )

                let crmUsers: any[] = []
                if (yClientsConnector?.connectorId) {
                    try {
                        const { data, error } = await getConnectionStatus(yClientsConnector.connectorId)
                        if (!error && data && data.status === 'active') {
                            crmUsers = await planService.getAvailableCrmUsers(this.projectId)
                        }
                    } catch (error) {
                        console.warn('Failed to check yClients connection status:', String(error))
                    }
                }

                const tasks = await planService.getAvailableTasks(this.projectId)
                this.availableTasks = tasks
                this.availableCrmUsers = crmUsers
            } catch (error) {
                console.error('Error loading plan data:', String(error))
            }
        },

        async createTask(taskData: any) {
            try {
                const newTask = await planService.createTask(taskData, this.projectId)

                // Добавляем новую задачу в список доступных задач
                this.availableTasks.push(newTask)

                // Возвращаем созданную задачу
                return newTask
            } catch (error) {
                // Обработка ошибки создания задачи
                console.error('Error creating task:', error)
                return null
            }
        },

        async onCreatePlan() {
            // Инициализируем selectedPlan для нового плана
            this.selectedPlan = {
                id: undefined,
                name: '',
                status: 'draft',
                taskIds: [],
                branchIds: [this.project?.name || ''], // Добавляем название проекта как первый филиал
                channelIds: ['general'],
                userIds: [],
                tasks: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            } as any

            // Обновляем список пользователей перед открытием модального окна
            try {
                // Проверяем статус коннектора yClients перед загрузкой CRM пользователей
                const yClientsConnector = this.$store.getters.projectById(this.projectId)?.connectors?.find(
                    (connector: any) => connector.connectorType === CONNECTOR_TYPES.yClients
                )

                if (yClientsConnector?.connectorId) {
                    const { data, error } = await getConnectionStatus(yClientsConnector.connectorId)
                    if (!error && data && data.status === 'active') {
                        this.availableCrmUsers = await planService.getAvailableCrmUsers(this.projectId)
                    } else {
                        this.availableCrmUsers = []
                    }
                } else {
                    this.availableCrmUsers = []
                }
            } catch (error) {
                console.warn('Failed to get CRM users:', String(error))
                this.availableCrmUsers = []
            }

            this.showCreatePlanModal = true
        },

        async onDeleteTask(task: Task) {
            // Используем стандартное подтверждение браузера
            const confirmed = confirm(
                `Вы действительно хотите удалить задачу "${task.name}"?\nЭто действие нельзя отменить.`
            )

            if (confirmed) {
                try {
                    await planService.deleteTask(task.id, this.projectId)

                    // Удаляем задачу из списка доступных задач
                    const index = this.availableTasks.findIndex(t => t.id === task.id)
                    if (index !== -1) {
                        this.availableTasks.splice(index, 1)
                    }

                    // Принудительно обновляем компонент
                    this.$forceUpdate()
                } catch (error) {
                    console.error('Error deleting task:', String(error))
                }
            }
        },

        async onEditPlan(plan: Plan) {
            this.selectedPlan = plan

            // Обновляем список пользователей перед открытием модального окна редактирования
            try {
                // Проверяем статус коннектора yClients перед загрузкой CRM пользователей
                const yClientsConnector = this.$store.getters.projectById(this.projectId)?.connectors?.find(
                    (connector: any) => connector.connectorType === CONNECTOR_TYPES.yClients
                )

                if (yClientsConnector?.connectorId) {
                    const { data, error } = await getConnectionStatus(yClientsConnector.connectorId)
                    if (!error && data && data.status === 'active') {
                        this.availableCrmUsers = await planService.getAvailableCrmUsers(this.projectId)
                    } else {
                        this.availableCrmUsers = []
                    }
                } else {
                    this.availableCrmUsers = []
                }
            } catch (error) {
                console.warn('Failed to get CRM users:', String(error))
                this.availableCrmUsers = []
            }

            this.showPlanEdit = true
        },

        async onCopyPlan(plan: Plan) {
            // Обновляем список пользователей перед открытием модального окна
            try {
                // Проверяем статус коннектора yClients перед загрузкой CRM пользователей
                const yClientsConnector = this.$store.getters.projectById(this.projectId)?.connectors?.find(
                    (connector: any) => connector.connectorType === CONNECTOR_TYPES.yClients
                )

                if (yClientsConnector?.connectorId) {
                    const { data, error } = await getConnectionStatus(yClientsConnector.connectorId)
                    if (!error && data && data.status === 'active') {
                        this.availableCrmUsers = await planService.getAvailableCrmUsers(this.projectId)
                    } else {
                        this.availableCrmUsers = []
                    }
                } else {
                    this.availableCrmUsers = []
                }
            } catch (error) {
                console.warn('Failed to get CRM users:', String(error))
                this.availableCrmUsers = []
            }

            // Создаем копию плана с предзаполненными данными
            const planCopy = {
                ...plan,
                id: undefined, // Убираем ID для создания нового плана
                name: `${plan.name} (копия)`,
                status: 'draft' as const,
                createdAt: undefined,
                updatedAt: undefined,

                // Копируем данные о дневной выручке
                dailyRevenuePlan: plan.dailyRevenuePlan
                    ? JSON.parse(JSON.stringify(plan.dailyRevenuePlan))
                    : undefined,
                generalDailyRevenuePlan: plan.generalDailyRevenuePlan,

                // Копируем данные о зарплате и бонусах
                salary: plan.salary ? JSON.parse(JSON.stringify(plan.salary)) : undefined,
                kpiBonusScale: plan.kpiBonusScale
                    ? JSON.parse(JSON.stringify(plan.kpiBonusScale))
                    : undefined,

                // Копируем старые поля для совместимости
                fixedDailySalary: plan.fixedDailySalary,
                dailyKpiBonus: plan.dailyKpiBonus,

                // Копируем задачи с нормами и лимитами
                tasks: plan.tasks
                    ? plan.tasks.map(task => ({
                          ...task,
                          norm: task.norm,
                          weight: task.weight,
                          maxCompletion: task.maxCompletion,
                      }))
                    : undefined,

                // Копируем нормы и веса задач
                taskNorms: plan.taskNorms ? JSON.parse(JSON.stringify(plan.taskNorms)) : undefined,
                taskWeights: plan.taskWeights ? JSON.parse(JSON.stringify(plan.taskWeights)) : undefined,
                maxCompletionLimits: plan.maxCompletionLimits
                    ? JSON.parse(JSON.stringify(plan.maxCompletionLimits))
                    : undefined,

                // Копируем шкалу выручки
                revenueScale: plan.revenueScale ? JSON.parse(JSON.stringify(plan.revenueScale)) : undefined,

                // Копируем филиалы и каналы
                branchIds: plan.branchIds ? [...plan.branchIds] : undefined,
                channelIds: ['general'],

                // Копируем пользователей
                users: plan.users ? JSON.parse(JSON.stringify(plan.users)) : undefined,
            }
            this.selectedPlan = planCopy
            this.showCreatePlanModal = true
        },

        async onActivatePlan(plan: Plan) {
            // Используем единый метод активации
            await this.activatePlan(plan)
        },

        async onDeletePlan(plan: Plan) {
            if (confirm(`Вы уверены, что хотите удалить план "${plan.name}"?`)) {
                try {
                    await planService.deletePlan(plan.id, this.projectId)
                    this.loadPlans()
                } catch (error) {
                    alert('Ошибка при удалении плана. Попробуйте еще раз.')
                }
            }
        },

        async onTogglePlanActivation(plan: Plan) {
            // Используем единый метод активации

            await this.activatePlan(plan)
        },

        async activatePlan(plan: Plan) {
            try {
                // // Сначала деактивируем все планы
                // const deactivatePromises = this.plans
                //     .filter(p => p.id !== plan.id && p.isActive)
                //     .map(p => planService.updatePlan(p.id, { isActive: false }, this.projectId))
                //
                // await Promise.all(deactivatePromises)

                // Затем активируем выбранный план
                await planService.updatePlan(
                    plan.id,
                    {
                        isActive: plan.isActive,
                    },
                    this.projectId
                )

                // Перезагружаем планы чтобы обновить состояние
                this.loadPlans()

                // Обновляем данные дашборда
                await this.loadPlanData()
                await this.loadFilterOptions()
                await this.loadShifts()
                this.calculateSummaryData()
            } catch (error) {
                console.error('Ошибка при активации плана:', error)
                // Обработка ошибки активации плана
            }
        },

        onPlanModalSubmit(formData: any) {
            // Проверяем, есть ли у плана ID - если есть, то обновляем, если нет - создаем
            if (this.selectedPlan && this.selectedPlan.id) {
                this.updatePlan(this.selectedPlan.id, formData)
            } else {
                this.createPlan(formData)
            }
        },

        async createPlan(formData: any) {
            try {
                // Данные уже в правильном формате от модалки
                const newPlan = await planService.createPlan(formData, this.projectId)
                this.showCreatePlanModal = false
                this.selectedPlan = newPlan
                this.showPlanEdit = true
                await this.loadPlans()

                // Переинициализируем данные в PlanSettings
                if (this.$refs.planEditWrapper && (this.$refs.planEditWrapper as any).reinitializePlanData) {
                    ;(this.$refs.planEditWrapper as any).reinitializePlanData()
                }
            } catch (error) {
                // Обработка ошибки создания плана
            }
        },

        async updatePlan(planId: string, formData: any) {
            try {
                await planService.updatePlan(planId, formData, this.projectId)
                this.showCreatePlanModal = false
                await this.loadPlans()

                // Обновляем выбранный план
                if (this.selectedPlan && this.selectedPlan.id === planId) {
                    const updatedPlan = await planService.getPlan(planId, this.projectId)
                    this.selectedPlan = updatedPlan

                    // Перезагружаем данные плана для отображения актуальной информации
                    await this.loadPlanData()

                    // Переинициализируем данные в PlanSettings
                    if (
                        this.$refs.planEditWrapper &&
                        (this.$refs.planEditWrapper as any).reinitializePlanData
                    ) {
                        ;(this.$refs.planEditWrapper as any).reinitializePlanData()
                    }
                }
            } catch (error) {
                // Обработка ошибки обновления плана
            }
        },

        onPlanSettingsSave(formData: any) {
            if (this.selectedPlan) {
                this.updatePlan(this.selectedPlan.id, formData)
            }
        },

        onPlanSettingsPublish(formData: any) {
            if (this.selectedPlan) {
                this.publishPlan(this.selectedPlan.id, formData)
            }
        },

        async publishPlan(planId: string, formData: any) {
            try {
                await planService.updatePlan(planId, { ...formData, status: 'published' }, this.projectId)
                this.showPlanSettings = false
                this.selectedPlan = null
                this.loadPlans()

                // Получаем обновленный план и устанавливаем его как выбранный
                const updatedPlan = await planService.getPlan(planId, this.projectId)
                this.selectedPlan = updatedPlan
            } catch (error) {
                // Обработка ошибки публикации плана
            }
        },

        onPlanCopy(plan: any) {
            // Создаем копию плана
            const copiedPlan = {
                ...plan,
                id: undefined,
                name: `${plan.name} (копия)`,
                status: 'draft',
                created_at: new Date().toISOString(),
            }
            this.selectedPlan = copiedPlan
            // Остаемся в режиме редактирования для копии
        },

        async onPlanDelete(plan: any) {
            try {
                await planService.deletePlan(plan.id, this.projectId)
                this.showPlanSettings = false
                this.selectedPlan = null
                this.loadPlans()
            } catch (error) {
                // Обработка ошибки удаления плана
            }
        },

        onPlanSettingsCancel() {
            this.showPlanSettings = false
            this.selectedPlan = null
        },

        onPlanEditClose() {
            this.showPlanEdit = false
            this.selectedPlan = null
        },

        async onOpenPlanSettingsModal(plan: Plan) {
            try {
                // Загружаем актуальные данные плана перед открытием модалки
                const fullPlan = await planService.getPlan(plan.id, this.projectId)
                this.selectedPlan = fullPlan
            } catch (error) {
                console.warn('Не удалось загрузить план по ID, используем данные из списка', String(error))
                this.selectedPlan = plan
            }

            // Обновляем список пользователей перед открытием модального окна редактирования
            try {
                // Проверяем статус коннектора yClients перед загрузкой CRM пользователей
                const yClientsConnector = this.$store.getters.projectById(this.projectId)?.connectors?.find(
                    (connector: any) => connector.connectorType === CONNECTOR_TYPES.yClients
                )

                if (yClientsConnector?.connectorId) {
                    const { data, error } = await getConnectionStatus(yClientsConnector.connectorId)
                    if (!error && data && data.status === 'active') {
                        this.availableCrmUsers = await planService.getAvailableCrmUsers(this.projectId)
                    } else {
                        this.availableCrmUsers = []
                    }
                } else {
                    this.availableCrmUsers = []
                }
            } catch (error) {
                console.warn('Failed to get CRM users:', String(error))
                this.availableCrmUsers = []
            }

            this.showCreatePlanModal = true
        },

        async onPlanEditSave(formData: any) {
            if (this.selectedPlan) {
                await this.updatePlan(this.selectedPlan.id, formData)
                this.onPlanEditClose()
            }
        },

        async onPlanEditPublish(formData: any) {
            if (this.selectedPlan) {
                await this.publishPlan(this.selectedPlan.id, formData)
                this.onPlanEditClose()
            }
        },

        onPlanEditCopy(plan: Plan) {
            this.onCopyPlan(plan)
        },

        async onPlanEditDelete(plan: Plan) {
            await this.onDeletePlan(plan)
            this.onPlanEditClose()
        },

        onPlansPageChange(page: number) {
            this.plansCurrentPage = page
            this.loadPlans()
        },

        async initialize() {
            this.loading = true
            try {
                // Сначала загружаем планы, чтобы определить активный план
                await this.loadPlans()
                await this.loadPlanData()

                // Затем настраиваем фильтры на основе активного плана
                await this.loadFilterOptions()

                // Загружаем остальные данные
                await this.loadShifts()
                this.calculateSummaryData()
            } catch (error) {
                // Обработка ошибки инициализации модуля
            } finally {
                this.loading = false
            }
        },
    },
    mounted() {
        this.initialize()
    },
})
